import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isRunningWorkout } from '../../utils/runningWorkoutMetrics';
import { useTranslation } from 'react-i18next';
import { IoChevronDownOutline } from 'react-icons/io5';
import type { WorkoutDetail } from '../../services/api';
import type { StreamPoint } from '../../utils/workoutStream';
import { secToHMS } from '../../utils/secToHMS';
import {
  canHighlightModalItem,
  findStreamIndicesForLap,
  formatPeakSelectionTitle,
  mapPeakModalRows,
  normalizeLapModalRows,
} from './workoutPeakHelpers';
import type { LapRow } from '../../utils/comparison/lapSegments';
import { NmTabNav } from '../../components/NmTabNav';

export const DATA_PICKER_TABS = [
  { id: 'laps', titleKey: 'workoutDetail.map.pickerLaps' },
  { id: 'pw', titleKey: 'workoutDetail.map.pickerPower' },
  { id: 'hr', titleKey: 'workoutDetail.map.pickerHr' },
  { id: 'sd', titleKey: 'workoutDetail.map.pickerSpeed' },
  { id: 'cd', titleKey: 'workoutDetail.map.pickerCadence' },
] as const;

function listForTab(workout: WorkoutDetail, tab: number): Array<Record<string, unknown>> {
  if (tab === 0) return normalizeLapModalRows(workout.details?.lap_data);
  if (tab === 1) return mapPeakModalRows(workout.details?.peaks?.pw_peak) as unknown as Array<Record<string, unknown>>;
  if (tab === 2) return mapPeakModalRows(workout.details?.peaks?.hr_peak) as unknown as Array<Record<string, unknown>>;
  if (tab === 3) return mapPeakModalRows(workout.details?.peaks?.sd_peak) as unknown as Array<Record<string, unknown>>;
  return mapPeakModalRows(workout.details?.peaks?.cd_peak) as unknown as Array<Record<string, unknown>>;
}

export function tabCounts(workout: WorkoutDetail): number[] {
  return DATA_PICKER_TABS.map((_, i) => listForTab(workout, i).length);
}

interface Props {
  workout: WorkoutDetail;
  stream: StreamPoint[];
  streamLoading: boolean;
  activeTab: number;
  onTabChange: (tab: number) => void;
  selectedKey: string | null;
  onRowClick: (item: Record<string, unknown>, idx: number, tab: number) => void;
  id?: string;
  embedded?: boolean;
}

export function WorkoutMapDataPicker({
  workout,
  stream,
  streamLoading,
  activeTab: selectedTab,
  onTabChange,
  selectedKey,
  onRowClick,
  id = 'wm-data-picker',
  embedded = false,
}: Props) {
  const { t } = useTranslation();
  const activeTab = embedded ? 0 : selectedTab;
  const [collapsed, setCollapsed] = useState(!embedded);
  const contentRef = useRef<HTMLDivElement>(null);
  const counts = useMemo(() => tabCounts(workout), [workout]);
  const rows = useMemo(() => listForTab(workout, activeTab), [workout, activeTab]);
  const workoutDurationSec = Number(workout.total_time ?? 0) || (stream.length ? stream[stream.length - 1]?.timeSeconds ?? 0 : 0) || 7200;
  const lapRows = useMemo(() => (workout.details?.lap_data ?? []) as LapRow[], [workout.details?.lap_data]);
  const peakUnit =
    activeTab === 1 ? 'W' : activeTab === 2 ? 'bpm' : activeTab === 3 ? 'kph' : activeTab === 4 ? (isRunningWorkout(workout) ? t('runningMetrics.cadenceUnit') : 'rpm') : '';

  const pickerTabs = useMemo(
    () =>
      DATA_PICKER_TABS.map((tab, i) => ({
        value: tab.id,
        label: (
          <span className="wm-data-picker__tab-label">
            <span>{t(tab.id === 'cd' && isRunningWorkout(workout) ? 'runningMetrics.cadence' : tab.titleKey)}</span>
            {counts[i] > 0 ? <small>{counts[i]}</small> : null}
          </span>
        ),
      })),
    [t, counts, workout],
  );

  const rowTitle = (item: Record<string, unknown>, idx: number) =>
    activeTab === 0
      ? t('workoutDetail.map.lapLabel', { n: Number(item.lap ?? idx) + 1 })
      : formatPeakSelectionTitle(item, t);

  const tabsId = `${id}-tabs`;
  const panelId = `${id}-panel`;
  const activeTabId = DATA_PICKER_TABS[activeTab]?.id ?? DATA_PICKER_TABS[0].id;

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (collapsed) content.setAttribute('inert', '');
    else content.removeAttribute('inert');
  }, [collapsed]);

  return (
    <section
      id={id}
      className={`${embedded ? "wm-data-picker--embedded " : ""}wm-data-picker wm-accordion-card${collapsed ? ' wm-data-picker--collapsed' : ' wm-accordion-card--expanded'}`}
      aria-label={t('workoutDetail.map.dataModal')}
    >
      {!embedded && <button
        type="button"
        className="wm-data-picker__heading wm-accordion-card__trigger"
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
        aria-controls={`${id}-content`}
      >
        <h3>{t('workoutDetail.map.effortsTitle')}</h3>
        <IoChevronDownOutline className="wm-data-picker__chevron wm-accordion-card__chevron" aria-hidden />
      </button>}

      <div
        ref={contentRef}
        id={`${id}-content`}
        className="wm-data-picker__content"
        aria-hidden={collapsed}
      >
        <div className="wm-data-picker__content-inner">
      {!embedded && <NmTabNav
        className="wm-data-picker__segment-tabs"
        items={pickerTabs}
        value={activeTabId}
        ariaLabel={t('workoutDetail.map.dataModal')}
        idBase={tabsId}
        panelId={panelId}
        onChange={(key) => {
              const idx = DATA_PICKER_TABS.findIndex((tab) => tab.id === key);
              if (idx >= 0) onTabChange(idx);
            }}
          />}

          <div
            id={panelId}
            className="wm-data-picker__list"
            role="tabpanel"
            aria-labelledby={embedded ? undefined : `${tabsId}-${activeTabId}`}
            aria-label={embedded ? t('workoutDetail.map.pickerLaps') : undefined}
          >
            {rows.map((item, idx) => {
              const rowKey = `${activeTab}-${idx}`;
              const clickable = !streamLoading && stream.length > 0 && (
                activeTab === 0
                  ? findStreamIndicesForLap(lapRows, idx, stream, workoutDurationSec) != null
                  : canHighlightModalItem(stream, item)
              );
              return (
                <button
                  key={rowKey}
                  type="button"
                  className={`wm-data-list__row wm-data-list__row--btn${selectedKey === rowKey ? ' wm-data-list__row--active' : ''}${!clickable ? ' wm-data-list__row--disabled' : ''}`}
                  disabled={!clickable}
                  onClick={() => onRowClick(item, idx, activeTab)}
                >
                  <span className="wm-data-list__title">{rowTitle(item, idx)}</span>
                  <span className="wm-data-list__value ltr-island">
                    {item.total_timer_time != null
                      ? secToHMS(Number(item.total_timer_time))
                      : item.value != null
                        ? activeTab === 3
                          ? Number(item.value).toFixed(1)
                          : String(Math.round(Number(item.value)))
                        : '—'}
                    {item.value != null && activeTab > 0 ? ` ${peakUnit}` : ''}
                  </span>
                </button>
              );
            })}
            {!rows.length && (
              <p className="wm-data-list__empty">{t('workoutDetail.map.noData')}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
